import React from 'react';
import { useSearchParams } from 'react-router';

import { Panel } from '@/components/ui/panel';
import {
    isFiltered,
    LiveTapeRows,
    matchesTapeFilter,
    parseTapeFilter,
    TapeFilterBar,
    tapeFilterParams,
    TapeFilterSheet,
    useLiveTape,
    useTapeMarks,
    type TapeFilter,
} from '@/features/liveTape';
import { useTokenPairStore } from '@/features/tokenPairs';
import Header from '@/features/trading/components/Header';
import { useIsMobile, useViewportInsets } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import type { ConnectionState } from '@/lib/ws';

/**
 * The public tape, as its own route.
 *
 * It began as a third tab in the terminal's 200px table, between Positions and
 * History, which put a feed of *everyone's* trades in the one panel that is
 * otherwise entirely about yours — and gave it seven rows of height. As a page
 * it gets the whole viewport and a place in the top nav, and the terminal's
 * table goes back to meaning "your positions and your history".
 *
 * The tape is always on here: `enabled` exists so a tab could switch the socket
 * off, and a route that is mounted IS the thing being looked at. Page
 * visibility is still folded in by the hook, so a backgrounded tab still drops
 * the connection.
 */

const LABEL: Record<ConnectionState, string> = {
    connected: 'Live',
    connecting: 'Connecting',
    reconnecting: 'Reconnecting',
    disconnected: 'Offline',
};

/** Whether the rows on screen are still arriving, or merely the last ones that did. */
const FeedPill: React.FC<{ state: ConnectionState }> = ({ state }) => (
    <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        <span
            className={cn(
                'inline-block size-1.5 rounded-full',
                state === 'connected'
                    ? 'animate-pulse bg-primary'
                    : 'bg-amber-400',
            )}
        />
        {LABEL[state]}
    </span>
);

const LiveTrades: React.FC = () => {
    // Same reason Trading.tsx calls it: this page owns the viewport and never
    // scrolls, so the shell has to size to the visible height.
    useViewportInsets();

    // The controls are a strip on a desktop and a bottom sheet on a phone, where
    // inline they wrapped to four rows before the first trade. Different trees,
    // not a media query, the same way Trading.tsx splits its panels.
    const isMobile = useIsMobile();

    const networkId = useTokenPairStore((s) => s.networkId);

    // The filter lives in the URL, not in state: it makes a narrowed tape — and
    // a trader's tape above all — a link someone can send.
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = React.useMemo(
        () => parseTapeFilter(searchParams),
        [searchParams],
    );
    const setFilter = React.useCallback(
        (next: TapeFilter) =>
            // replace, not push: a filter is not somewhere the viewer navigated
            // to, and stacking every chip in history makes Back useless
            setSearchParams(tapeFilterParams(next), { replace: true }),
        [setSearchParams],
    );

    // `trader` is the only filter the server serves; the rest narrow what is
    // already here. Both halves are applied below, because the socket keeps
    // pushing every trader's rows regardless of what was asked for.
    const tape = useLiveTape(networkId, true, filter.trader);
    // prices the open rows; shares the terminal's markets cache entry
    const marks = useTapeMarks(networkId);

    const visible = React.useMemo(
        () => tape.trades.filter((trade) => matchesTapeFilter(trade, filter)),
        [tape.trades, filter],
    );

    // only markets actually on the tape can match, so those are the ones offered
    const coins = React.useMemo(() => {
        const present = new Set<string>();
        for (const trade of tape.trades) {
            if (trade.coin) present.add(trade.coin.toUpperCase());
        }
        return [...present].sort();
    }, [tape.trades]);

    const narrowed = isFiltered(filter);

    return (
        <div className="bento-grid tape-page text-foreground">
            <Header />

            <Panel
                as="section"
                aria-label="Live trades"
                className="flex min-h-0 flex-col overflow-hidden"
                style={{ gridArea: 'tape' }}
            >
                <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-4 py-2.5">
                    <h2 className="text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">
                        Live Trades
                    </h2>
                    <span className="rounded-full bg-primary px-1.5 py-px font-mono text-[9px] font-bold text-primary-foreground">
                        {visible.length}
                    </span>
                    {/* Unqualified, the count reads as the size of the feed
                        while a filter quietly removes most of it. */}
                    {narrowed && (
                        <span className="font-mono text-[10px] text-muted-foreground">
                            of {tape.trades.length}
                        </span>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                        Every position opened and closed on Snappy, as it
                        happens. Open trades show{' '}
                        <span className="text-foreground/70">≈</span> unrealized
                        PnL at the current mark.
                    </p>
                    <span className="ml-auto flex items-center gap-2">
                        {isMobile && (
                            <TapeFilterSheet
                                filter={filter}
                                onChange={setFilter}
                                coins={coins}
                                matching={visible.length}
                            />
                        )}
                        <FeedPill state={tape.connection} />
                    </span>
                </div>

                {!isMobile && (
                    <div className="shrink-0 border-b border-border px-4 py-2">
                        <TapeFilterBar
                            filter={filter}
                            onChange={setFilter}
                            coins={coins}
                        />
                    </div>
                )}

                <div className="min-h-0 flex-1 overflow-y-auto">
                    <LiveTapeRows
                        tape={tape}
                        marks={marks}
                        trades={visible}
                        filtered={narrowed}
                        onSelectTrader={(trader) =>
                            setFilter({ ...filter, trader })
                        }
                    />
                </div>

                {/* Traders are named, not identified, and the line says so. */}
                <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-4 py-2.5 font-mono text-[9px] uppercase tracking-[0.08em] text-muted-foreground">
                    <span>Names are derived from the trading address</span>
                </div>
            </Panel>
        </div>
    );
};

LiveTrades.displayName = 'LiveTrades';
export default LiveTrades;
